// 인스펙터 (docs/plans/e2-scene.md 마일스톤 3). 활성 씬에서 선택한 오브젝트의 속성.
//   공통: id (초점을 잃거나 Enter 로 이름 바꾸기), x, y (타이핑은 한 세션이 되돌리기 한 번), 표시 여부
//   타입별: registerObjectType 의 Inspector 컴포넌트 (코어 타입은 scene/coreTypes.tsx)
//   스크립트: 논리 이름 목록, 열기, 순서, 떼기, 붙이기 (없으면 템플릿으로 만든다). 컴포넌트마다 매개변수 폼 (ComponentParams)
//   맨 아래: 검사 결과 (document.problems)
// 여러 개를 골랐으면 공통 칸만 보이고 값이 다르면 "여러 값" 이다. 변경은 전부 editor.sceneTools 를 거쳐 명령이 된다.

import { graphPathOfComponent, isGraphPath, type SceneDocument, type SceneObject } from "@initial-editor/core";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useEditor } from "../../editor/EditorContext";
import { asMapDocument } from "../../editor/maps/schemaStore";
import { openAttachScriptDialog } from "../../editor/scene/AttachScriptDialog";
import { compoundCommand } from "../../editor/scene/commands";
import { inspectorFor } from "../../editor/scene/coreTypes";
import { FieldRow, NumberField } from "@initial-editor/ui";
import { TypeIcon } from "../../editor/scene/typeIcons";
import { MapObjectInspector } from "../maps/MapObjectInspector";
import { ComponentParams, SceneLoaderNote } from "./ComponentParams";
import "./InspectorPanel.css";

export const INSPECTOR_EMPTY = "활성 씬 탭 없음. 씬 탭에서 오브젝트를 선택하면 속성이 표시됩니다.";

function sameValue<T>(values: T[]): T | null {
  return values.length > 0 && values.every((v) => v === values[0]) ? values[0] : null;
}

const IdField = observer(function IdField({ object }: { object: SceneObject }) {
  const editor = useEditor();
  const [text, setText] = useState(object.id);
  const [focused, setFocused] = useState(false);
  // Escape로 초점을 놓으면 blur에서 이름을 바꾸지 않는다
  const cancelled = useRef(false);
  useEffect(() => {
    if (!focused) setText(object.id);
  }, [object.id, focused]);
  const commit = () => {
    if (text.trim() !== object.id && !editor.sceneTools.rename(object.id, text)) setText(object.id);
  };
  return (
    <input
      className="input field-text"
      value={text}
      aria-label="id"
      data-testid="inspector-id"
      onChange={(e) => setText(e.target.value)}
      onFocus={() => {
        cancelled.current = false;
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
        if (cancelled.current) {
          cancelled.current = false;
          setText(object.id);
          return;
        }
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelled.current = true;
          setText(object.id);
          e.currentTarget.blur();
        }
      }}
    />
  );
});

const CommonFields = observer(function CommonFields({ doc, objects }: { doc: SceneDocument; objects: SceneObject[] }) {
  const editor = useEditor();
  const tools = editor.sceneTools;
  const ids = objects.map((o) => o.id);
  const key = ids.join(",");
  const visibleRef = useRef<HTMLInputElement>(null);
  const visible = sameValue(objects.map((o) => o.visible));
  useEffect(() => {
    if (visibleRef.current) visibleRef.current.indeterminate = visible === null;
  }, [visible]);

  const setAxis = (axis: "x" | "y", value: number, session: string) => {
    if (objects.length === 1) {
      tools.apply(doc, doc.scene.setField(objects[0].id, axis, value, session));
      return;
    }
    tools.apply(
      doc,
      doc.scene.moveObjects(
        objects.map((o) => ({ id: o.id, x: axis === "x" ? value : o.x, y: axis === "y" ? value : o.y })),
        session,
      ),
    );
  };
  const setVisible = (value: boolean) => {
    if (objects.length === 1) {
      tools.setVisible(objects[0].id, value);
      return;
    }
    tools.apply(
      doc,
      compoundCommand(
        `표시 여부: ${objects.length}개`,
        objects.map((o) => doc.scene.setField(o.id, "visible", value)),
      ),
    );
  };

  return (
    <div className="inspector-section" data-testid="inspector-common">
      {objects.length === 1 && (
        <FieldRow label="id" hint="씬 안에서 고유해야 합니다">
          <IdField object={objects[0]} />
        </FieldRow>
      )}
      <FieldRow label="x">
        <NumberField value={sameValue(objects.map((o) => o.x))} onChange={(v, s) => setAxis("x", v, s)} sessionPrefix={`common:${key}:x`} step={1} testId="inspector-x" ariaLabel="x" />
      </FieldRow>
      <FieldRow label="y">
        <NumberField value={sameValue(objects.map((o) => o.y))} onChange={(v, s) => setAxis("y", v, s)} sessionPrefix={`common:${key}:y`} step={1} testId="inspector-y" ariaLabel="y" />
      </FieldRow>
      <FieldRow label="표시" hint="끄면 렌더링을 생략하고 컴포넌트의 render를 호출하지 않습니다 (update는 호출합니다)">
        <input ref={visibleRef} type="checkbox" checked={visible === true} onChange={(e) => setVisible(e.target.checked)} data-testid="inspector-visible" aria-label="표시" />
        {visible === null && <span className="muted field-mixed">여러 값</span>}
      </FieldRow>
    </div>
  );
});

const ScriptsSection = observer(function ScriptsSection({ object }: { object: SceneObject }) {
  const editor = useEditor();
  const tools = editor.sceneTools;
  const language = editor.project.gameJson.script;
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (name: string) => {
    const next = new Set(folded);
    if (!next.delete(name)) next.add(name);
    setFolded(next);
  };
  const open = async (name: string) => {
    // 그래프에서 만든 컴포넌트면 그래프를 연다 (생성 코드가 아니라)
    const graph = graphPathOfComponent(name);
    if (isGraphPath(graph) && (await editor.backend.exists(graph).catch(() => false))) {
      await editor.openPath(graph);
      return;
    }
    const path = tools.scriptPath(name);
    if (!(await editor.backend.exists(path).catch(() => false))) {
      editor.toasts.warn(`파일 없음: ${path} (스크립트 추가 대화상자에서 생성 가능)`);
      return;
    }
    await editor.openPath(path);
  };
  return (
    <div className="inspector-section" data-testid="inspector-scripts">
      <div className="inspector-subtitle">
        스크립트 <span className="muted">({language === "mruby" ? "scripts/ruby" : "scripts/lua"} 기준 논리 이름)</span>
      </div>
      {object.scripts.length === 0 && <div className="muted inspector-note">추가된 스크립트 없음</div>}
      {object.scripts.length > 0 && <SceneLoaderNote />}
      {object.scripts.map((name, i) => (
        <div key={name} className="inspector-component" data-testid="inspector-component" data-name={name}>
          <div className="inspector-script" data-testid="inspector-script-row" data-name={name}>
            <button
              type="button"
              className="btn btn-ghost inspector-script-fold"
              onClick={() => toggle(name)}
              aria-expanded={!folded.has(name)}
              aria-label={`${name} 매개변수 ${folded.has(name) ? "펼치기" : "접기"}`}
              title={folded.has(name) ? "매개변수 펼치기" : "매개변수 접기"}
              data-testid="inspector-script-fold"
            >
              {folded.has(name) ? "▸" : "▾"}
            </button>
            <span className="inspector-script-order muted" title="엔진이 부르는 순서">
              {i + 1}
            </span>
            <span className="inspector-script-name" title={tools.scriptPath(name)}>
              {name}
            </span>
            <button type="button" className="btn btn-ghost" onClick={() => void open(name)} data-testid="inspector-script-open" title={tools.scriptPath(name)}>
              열기
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={i === 0}
              onClick={() => tools.moveScript(object.id, name, -1)}
              aria-label={`${name} 위로 이동`}
              title="위로 이동 (먼저 실행)"
              data-testid="inspector-script-up"
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={i === object.scripts.length - 1}
              onClick={() => tools.moveScript(object.id, name, 1)}
              aria-label={`${name} 아래로 이동`}
              title="아래로 이동 (나중에 실행)"
              data-testid="inspector-script-down"
            >
              ↓
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => tools.detachScript(object.id, name)} aria-label={`${name} 제거`} data-testid="inspector-script-remove" title="제거 (이 컴포넌트의 매개변수 값도 삭제)">
              ×
            </button>
          </div>
          {!folded.has(name) && <ComponentParams object={object} name={name} />}
        </div>
      ))}
      <button type="button" className="btn inspector-attach" onClick={() => void openAttachScriptDialog(editor, object.id)} data-testid="inspector-attach">
        스크립트 추가
      </button>
    </div>
  );
});

const ProblemsSection = observer(function ProblemsSection({ doc }: { doc: SceneDocument }) {
  const problems = doc.problems;
  return (
    <div className="inspector-section inspector-problems" data-testid="inspector-problems" data-count={problems.length}>
      <div className="inspector-subtitle">검사 {problems.length === 0 ? <span className="muted">(문제 없음)</span> : <span className="inspector-problem-count">{problems.length}</span>}</div>
      {problems.map((p, i) => (
        <div key={i} className={"inspector-problem is-" + p.severity} data-testid="inspector-problem">
          <span className="inspector-problem-where">{p.location ?? ""}</span>
          <span>{p.message}</span>
        </div>
      ))}
    </div>
  );
});

export const InspectorPanel = observer(function InspectorPanel() {
  const editor = useEditor();
  // 맵 문서는 맵 오브젝트 인스펙터가 그린다 (components/maps/MapObjectInspector.tsx)
  if (asMapDocument(editor.documents.active)) return <MapObjectInspector />;
  const tools = editor.sceneTools;
  const doc = tools.activeScene;
  if (!doc) {
    return (
      <div className="panel-body" data-testid="inspector">
        <div className="panel-hint">{INSPECTOR_EMPTY}</div>
      </div>
    );
  }
  const objects = tools.selectedObjects;
  if (objects.length === 0) {
    return (
      <div className="inspector" data-testid="inspector" data-selection="0">
        <div className="inspector-head">
          <span className="inspector-title">{doc.scene.name || doc.title}</span>
          <span className="muted">씬, 오브젝트 {doc.scene.objects.length}개</span>
        </div>
        <div className="panel-hint">선택한 오브젝트 없음. 계층이나 씬 뷰에서 선택하면 속성이 표시됩니다.</div>
        <ProblemsSection doc={doc} />
      </div>
    );
  }
  const single = objects.length === 1 ? objects[0] : null;
  const spec = single ? tools.typeSpec(single.type) : undefined;
  const Inspector = single ? inspectorFor(spec, single.type) : null;
  return (
    <div className="inspector" data-testid="inspector" data-selection={objects.length}>
      <div className="inspector-head">
        {single ? (
          <>
            <span className="inspector-type-icon">
              <TypeIcon icon={spec?.icon ?? single.type} size={16} />
            </span>
            <span className="inspector-title">{single.id}</span>
            <span className="muted">{spec?.label ?? single.type}</span>
          </>
        ) : (
          <span className="inspector-title">{objects.length}개 선택</span>
        )}
      </div>
      <div className="inspector-body">
        <CommonFields doc={doc} objects={objects} />
        {single && Inspector && <Inspector document={doc} object={single} />}
        {single && !Inspector && <div className="muted inspector-note">타입별 인스펙터 없음: {single.type}</div>}
        {single && <ScriptsSection object={single} />}
        {!single && <div className="muted inspector-note">여러 오브젝트 선택됨. 타입별 속성과 스크립트는 1개만 선택하면 표시됩니다.</div>}
        <ProblemsSection doc={doc} />
      </div>
    </div>
  );
});
